import { isAxiosError } from 'axios';
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    ContentTypeEnumSchema,
    JsonContentTypeEnumSchema,
    MessageParticipantRole,
    MessageType,
    ResponseMessage,
    SendJsonContentTypeSchema,
} from '@amzn/ring-cs-chatbot-service-model';
import { ManagedSession } from '../api/managed/ManagedSession';
import { SessionStatus } from '../api/sessions/Session';
import { IntentFeedback } from '../types/enums';
import { translated } from '../utils/TranslationUtil';

// Interactive elements configuration - elements that support selectedOption for state restoration
const INTERACTIVE_ELEMENTS = ['select', 'multiselect', 'rating'] as const;

// Checks if HTML content contains any interactive elements
const hasInteractiveElement = (content: string | null | undefined): boolean => {
    if (!content) return false;
    return INTERACTIVE_ELEMENTS.some(element => {
        const regex = new RegExp(`<${element}(\\s|>)`, 'i');
        return regex.test(content);
    });
};

type Message = ResponseMessage & {
    content: string;
}

type MessageGroup = {
    participantRole: MessageParticipantRole;
    timestamp: number;
    messages: Message[];
}

export const useMessages = (session: ManagedSession) => {
    const [initialTranscriptLoading, setInitialTranscriptLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [messageError, setMessageError] = useState('');
    const [sendingCustomerMessage, setSendingCustomerMessage] = useState<string | null>(null);
    const [selectedOptions, setSelectedOptions] = useState<{ [key: string]: string }>({});
    const [transcriptNextToken, setTranscriptNextToken] = useState<string | undefined>(undefined);

    const sessionStatus = session.useSessionStatus();
    const connecting = useMemo(() => {
        return [SessionStatus.CREATED, SessionStatus.CONNECTING].includes(sessionStatus);
    }, [sessionStatus]);

    // Get pending messages from session
    const pendingMessage = session.usePendingMessage();

    const getTranscript = useCallback(async (pNextToken?: string) => {
        setLoading(true);
        try {
            console.log('retrieving transcript');
            const transcriptResult = await session.transcript({ nextToken: pNextToken });
            const { transcript, nextToken } = transcriptResult;
            setTranscriptNextToken(nextToken);
        } finally {
            setLoading(false);
            /**
             * Utilized for initial Chat body loading experience only
             * For after session has connected prior to transcript loading
             */
            setInitialTranscriptLoading(false);
        }
    }, [session]);

    const sendMessage = useCallback(async (message: string) => {
        setSendingCustomerMessage(message);
        setLoading(true);
        setMessageError('');
        try {
            await session.sendMessage({
                message: {
                    type: MessageType.MESSAGE,
                    content: message,
                    contentType: ContentTypeEnumSchema.enum['text/plain'],
                },
            });
        } catch (error) {
            console.error(`Failed to send message: ${error}`)
            if (isAxiosError(error) && !error.response) {
                setMessageError(translated('Components.Alert.networkError'));
            } else {
                setMessageError(translated('Components.Alert.messageError'));
            }
        } finally {
            setLoading(false);
            setSendingCustomerMessage(null);
        }
    }, [session]);

    const sendFeedback = useCallback(async (feedback: IntentFeedback, attributes?: Record<string, string>) => {
        setLoading(true);
        try {
            await session.sendMessage({
                message: {
                    type: MessageType.MESSAGE,
                    contentType: ContentTypeEnumSchema.enum['application/json'],
                    content: JSON.stringify({
                        type: feedback === IntentFeedback.THUMBS_UP ?
                            SendJsonContentTypeSchema.enum['application/sherlock.event.feedback.thumbs.up'] :
                            SendJsonContentTypeSchema.enum['application/sherlock.event.feedback.thumbs.down'],
                        attributes,
                    }),
                },
            });
        } finally {
            setLoading(false);
        }
    }, [session]);

    const sendSurvey = useCallback(async () => {
        setLoading(true);

        try {
            await session.sendMessage({
                message: {
                    type: MessageType.MESSAGE,
                    contentType: ContentTypeEnumSchema.enum['application/json'],
                    content: JSON.stringify({
                        type: SendJsonContentTypeSchema.enum['application/sherlock.event.end.request'],
                    }),
                },
            });
        } catch (error) {
            console.error('Failed to send survey request:', error);
        } finally {
            setLoading(false);
        }
    }, [session]);

    const loadPreviousMessages = useMemo(() => {
        if (!transcriptNextToken) return undefined;

        return async () => {
            setLoading(true);
            try {
                const { transcript, nextToken } = await session.transcript({ nextToken: transcriptNextToken });
                setTranscriptNextToken(nextToken);
            } catch (error) {
                console.error('Failed to load previous messages:', error);
            } finally {
                setLoading(false);
            }
        };
    }, [session, transcriptNextToken]);

    useEffect(() => {
        session.connect()
            .catch(() => {
                console.error('Failed to connect to session');
            });

        session.onConnectionEstablished({
            handler: () => {
                console.log('connection established');
                getTranscript();
            },
        });
        session.onConnectionBroken({
            handler: () => {
                console.log('connection broken');
            },
        });
    }, [session]);

    /**
     * The SendMessage API forwards the sent message to the websocket, may
     * perform some additional work, and then completes. This can cause a race
     * condition between the message coming from the socket and the API
     * response, and if the socket arrives first, the sent message will appear
     * on the UI immediately. Clear the pending message here to ensure the sent
     * message is not shown twice.
     */
    useEffect(() => {
        const unsubscribe = session.onMessage({
            handler: (message: ResponseMessage) => {
                if (message.type === MessageType.MESSAGE && message.participantRole === MessageParticipantRole.CUSTOMER) {
                    setSendingCustomerMessage(null);
                }
            },
        });

        return () => unsubscribe();
    }, [session]);

    const messages = session.useMessages();

    // Track active participant roles
    const currentParticipantRoles = useMemo(() => {
        const roles = new Set<MessageParticipantRole>();

        messages.forEach((message) => {
            //  If the participant leaves, remove role
            if (message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.left']) {
                roles.delete(message.participantRole);
            } else {
                roles.add(message.participantRole);
            }
        });

        return roles;
    }, [messages]);

    // Track previously selected <select> and <rating> values to support page refreshes
    const selectedOptionsMemo = useMemo(() => {
        const selectedOptions: { [key: string]: string } = {};

        let lastInteractiveMessageId: string | undefined = undefined;
        messages
            .filter(message => message.type === MessageType.MESSAGE)
            .filter(message => message.contentType === ContentTypeEnumSchema.enum['text/plain'])
            .forEach(message => {
                if (hasInteractiveElement(message.content)) {
                    lastInteractiveMessageId = message.messageId;
                } else if (lastInteractiveMessageId) {
                    selectedOptions[lastInteractiveMessageId] = message.content || '';
                    lastInteractiveMessageId = undefined;
                }
            });

        return selectedOptions;
    }, [messages]);

    // Only runs when messages change
    useEffect(() => {
        setSelectedOptions(selectedOptionsMemo);
    }, [selectedOptionsMemo]);

    const typingRole = session.useTyping();

    const groupMessagesByRole = useMemo(() => {
        const groupedMessages: (MessageGroup | Message)[] = [];
        let currentGroup: MessageGroup | null = null;
        const messagesToDisplay: Message[] = messages.filter((message): message is Message => {
            return (
                message.contentType === ContentTypeEnumSchema.enum['text/plain'] ||
                message.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/sherlock.event.feedback.requested'] ||
                message.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/sherlock.message.bedrock.response'] ||
                message.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/chat.message.sherlock.response'] ||
                message.jsonContent?.type === JsonContentTypeEnumSchema.enum['application/chat.message.vqa.response'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.joined'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.left'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.idle'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.returned']
            );
        });

        messagesToDisplay.forEach((message) => {
            if (message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.joined'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.left'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.idle'] ||
                message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.participant.returned']) {
                if (currentGroup) {
                    groupedMessages.push(currentGroup);
                    currentGroup = null;
                }
                groupedMessages.push(message);
            } else {
                if (currentGroup && currentGroup.participantRole === message.participantRole) {
                    currentGroup.messages.push(message);
                    currentGroup.timestamp = message.absoluteTime;
                } else {
                    if (currentGroup) {
                        groupedMessages.push(currentGroup);
                    }
                    currentGroup = {
                        participantRole: message.participantRole,
                        timestamp: message.absoluteTime,
                        messages: [message],
                    };
                }
            }
        });

        if (currentGroup) {
            groupedMessages.push(currentGroup);
        }

        return groupedMessages;
    }, [messages]);

    // We only show body when connection is established AND transcript has loaded
    const initialLoading = connecting || initialTranscriptLoading;

    return {
        currentParticipantRoles,
        groupMessagesByRole,
        initialLoading,
        loading,
        loadPreviousMessages,
        messageError,
        sendingCustomerMessage,
        pendingMessage,
        selectedOptions,
        sendFeedback,
        sendMessage,
        sendSurvey,
        setLoading,
        typingRole,
    };
};
